import { ScriptingLanguage } from "@octopusdeploy/octopus-server-client";
import type { KubernetesAgentConfigurationResource } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/KubernetesAgentConfigurationDialog";
import type { Language, TextFormat } from "~/components/CodeEditor/CodeEditor";
export interface EnvironmentResourceSubset {
    Id: string;
    Name: string;
    Slug?: string;
}
// This value represents the highest major version of the Kubernetes Agent Helm Chart
// that is supported by this version of Server.
// If this value is being bumped, please also bump the value in the backend:
// KubernetesAgentUpgradeManager.LatestSupportedMajorVersion
const kubernetesAgentLatestSupportedMajorVersion = 1;
/**
 * DNS labels can only be lowercase alphanumeric (with hyphens), and can't start/end with hyphens.
 * See https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#dns-label-names
 * Note that Helm has slightly different requirements in certain circumstances. For example, release names
 * can only be a max of 53 characters.
 * @param value
 * @param maxLength
 */
const getAsValidDnsLabel = (value: string, maxLength: number = 63) => value
    .replace(/[^0-9a-z-]/gi, "") // Only alphanumeric and hyphens
    .replace(/^-+|-+$/g, "") // No hyphens at start or end.
    .toLowerCase()
    .substring(0, maxLength);
const getValidReleaseName = (targetName: string) => getAsValidDnsLabel(targetName, 53); // Release names have a max length of 53, see https://helm.sh/docs/chart_template_guide/getting_started/#adding-a-simple-template-call
const getValidTargetName = (targetName: string) => escapeAllDoubleQuotes(targetName);
const escapeAllDoubleQuotes = (value: string) => value.replace(/"/g, "\\\"");
function getEnvironmentSlugsForIds(environmentIds: string[], environments: EnvironmentResourceSubset[]) {
    return environmentIds
        .map((id) => environments.find((env) => env.Id === id)?.Slug ?? "")
        .filter((slug) => slug !== "") // This shouldn't do anything, because slugs are required on environments
        .join(",");
}
function getMachineRolesList(machineRoles: string[]) {
    return machineRoles.map((mr) => escapeAllDoubleQuotes(mr)).join(","); // Machine roles can contain double quotes, so we'll escape them so they don't break the helm command
}
export function createHelmCommand(language: ScriptingLanguage | Language | TextFormat, model: KubernetesAgentConfigurationResource, environments: EnvironmentResourceSubset[]) {
    const lineBreak = language === ScriptingLanguage.Bash ? "\\" : "`";
    const targetName = getValidTargetName(model.Name);
    const machineRoles = getMachineRolesList(model.MachineRoles);
    const environmentSlugs = getEnvironmentSlugsForIds(model.EnvironmentIds, environments);
    const namespace = generateNamespace(model.Name);
    const releaseName = generateReleaseName(model.Name);
    if (!model.Space) {
        throw new Error("Attempted to render Kubernetes Agent Configuration Dialog outside of a space. This should never happen.");
    }
    let setValues = `--set agent.acceptEula="Y" ${lineBreak}
--set agent.targetName="${targetName}" ${lineBreak}
--set agent.serverUrl="${model.ServerUri}" ${lineBreak}
--set agent.serverCommsAddress="${model.ServerCommsAddress}" ${lineBreak}
--set agent.space="${model.Space.Name}" ${lineBreak}
--set agent.targetEnvironments="{${environmentSlugs}}" ${lineBreak}
--set agent.targetRoles="{${machineRoles}}" ${lineBreak}`;
    if (model.DefaultNamespace) {
        setValues += `
--set agent.defaultNamespace="${model.DefaultNamespace}" ${lineBreak}`;
    }
    setValues += `
--set agent.bearerToken="${model.AccessToken}" ${lineBreak}`;
    if (!!model.StorageClassName) {
        //add the persistence options after the initial set
        setValues += `
--set persistence.storageClassName="${model.StorageClassName}" ${lineBreak}`;
    }
    return `helm upgrade --install --atomic ${lineBreak}
${setValues}
--version "${kubernetesAgentLatestSupportedMajorVersion}.*.*" ${lineBreak}
--create-namespace --namespace ${namespace} ${lineBreak}
${releaseName} ${lineBreak}
oci://registry-1.docker.io/octopusdeploy/kubernetes-agent`;
}
export function generateNamespace(targetName: string): string {
    const validTargetName = getValidTargetName(targetName);
    return getAsValidDnsLabel(`octopus-agent-${validTargetName}`);
}
export function generateReleaseName(targetName: string): string {
    return getValidReleaseName(targetName);
}
